// apps/client/src/ui-kit/MissionItem.tsx
//
// Mission Item — V2 white card, English copy, optimistic pulsing CLAIM button
// Features: Animated progress bar, pulsing claim button, celebration on claim
//

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
import Icon from './Icon';
import { useAudio } from '../providers/AudioProvider';

export interface MissionData {
    id: string;
    name: string;
    description: string;
    icon: string | null;
    type: 'DAILY' | 'WEEKLY' | 'PERMANENT';
    targetCount: number;
    rewardChips: number;
    currentCount: number;
    status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'CLAIMED';
    canClaim: boolean;
    targetUrl?: string | null;
    action?: string;
}

interface MissionItemProps {
    mission: MissionData;
    onClaim: (id: string) => Promise<void>;
    index?: number;
}

export function MissionItem({ mission, onClaim, index = 0 }: MissionItemProps) {
    const [isClaiming, setIsClaiming] = useState(false);
    const [showCelebration, setShowCelebration] = useState(false);
    const { playClaim } = useAudio();

    const progress = Math.min((mission.currentCount / mission.targetCount) * 100, 100);
    const isCompleted = mission.status === 'COMPLETED';
    const isClaimed = mission.status === 'CLAIMED';
    const canClaim = mission.canClaim && !isClaiming;

    // Icon mapping for mission actions
    const actionIcons: Record<string, string> = {
        DAILY_LOGIN: 'login',
        WATCH_AD: 'play_circle',
        PLAY_GAME: 'sports_esports',
        WIN_LINE: 'check',
        WIN_TWO_LINES: 'done_all',
        WIN_BINGO: 'celebration',
        INVITE_FRIEND: 'person_add',
    };

    const handleClaim = async () => {
        if (!canClaim) return;

        setIsClaiming(true);
        try {
            playClaim();
            await onClaim(mission.id);
            setShowCelebration(true);
            setTimeout(() => setShowCelebration(false), 2000);
        } finally {
            setIsClaiming(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.08 }}
            className={`relative glass-panel rounded-3xl p-4 transition-all ${isClaimed
                ? 'border-green-500/30 opacity-60'
                : canClaim
                    ? 'border-[#FBBF24]/50 shadow-glow-gold'
                    : 'border-white/10'
                }`}
        >
            {/* Celebration overlay */}
            <AnimatePresence>
                {showCelebration && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.5 }}
                        className="absolute inset-0 flex items-center justify-center bg-green-500/20 backdrop-blur-sm rounded-2xl z-10"
                    >
                        <div className="text-center">
                            <motion.div
                                animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                                transition={{ duration: 0.5 }}
                                className="text-5xl mb-2"
                            >
                                🎉
                            </motion.div>
                            <span className="text-[#FFC107] font-black text-lg">+{mission.rewardChips}</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="flex items-start gap-4">
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isClaimed
                    ? 'bg-green-500/20'
                    : isCompleted
                        ? 'bg-[#FFC107]/20'
                        : 'bg-white/5'
                    }`}>
                    <Icon
                        name={isClaimed ? 'check_circle' : (mission.icon || actionIcons[mission.name] || 'task_alt')}
                        className={`text-xl ${isClaimed
                            ? 'text-green-400'
                            : isCompleted
                                ? 'text-[#FFC107]'
                                : 'text-gray-400'
                            }`}
                    />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <h3 className="text-white font-bold text-sm drop-shadow-sm">{mission.name}</h3>
                            <p className="text-white/60 text-xs mt-0.5 line-clamp-1">{mission.description}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                            <div className="text-[#FBBF24] font-black text-sm">+{mission.rewardChips}</div>
                            <div className="text-[10px] text-white/40">BingoChips</div>
                        </div>
                    </div>

                    {/* Progress section */}
                    <div className="mt-3">
                        {/* Progress text */}
                        <div className="flex justify-between text-[10px] mb-1">
                            <span className={isCompleted ? 'text-green-400 font-bold' : 'text-gray-500'}>
                                {mission.currentCount}/{mission.targetCount}
                            </span>
                            <span className={isCompleted ? 'text-green-400' : 'text-gray-500'}>
                                {isClaimed ? '✓ Claimed' : `${Math.round(progress)}%`}
                            </span>
                        </div>

                        {/* Progress bar */}
                        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${progress}%` }}
                                transition={{ duration: 0.6, delay: index * 0.08, ease: 'easeOut' }}
                                className={`h-full rounded-full ${isClaimed
                                    ? 'bg-green-500'
                                    : isCompleted
                                        ? 'bg-gradient-to-r from-[#FFC107] to-[#E88A00]'
                                        : 'bg-white/30'
                                    }`}
                            />
                        </div>
                    </div>

                    {/* Claim button - Pulsing animation when available */}
                    {canClaim ? (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-4"
                        >
                            <motion.div
                                animate={{
                                    boxShadow: [
                                        '0 0 0 0 rgba(255, 193, 7, 0.4)',
                                        '0 0 0 10px rgba(255, 193, 7, 0)',
                                    ]
                                }}
                                transition={{ duration: 1.5, repeat: Infinity }}
                                className="rounded-xl"
                            >
                                <Button
                                    onClick={handleClaim}
                                    size="sm"
                                    fullWidth
                                    icon="redeem"
                                    isLoading={isClaiming}
                                >
                                    CLAIM!
                                </Button>
                            </motion.div>
                        </motion.div>
                    ) : (mission.targetUrl && mission.status === 'IN_PROGRESS') ? (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-4"
                        >
                            <Button
                                onClick={() => {
                                    if (mission.targetUrl) {
                                        // Open standard link; WebApp handles t.me links nicely if used proper API, but simple window.open works
                                        if (mission.targetUrl.startsWith('https://t.me/')) {
                                            window.Telegram?.WebApp?.openTelegramLink(mission.targetUrl) || window.open(mission.targetUrl, '_blank');
                                        } else {
                                            window.Telegram?.WebApp?.openLink(mission.targetUrl) || window.open(mission.targetUrl, '_blank');
                                        }
                                        
                                        // The backend will verify membership when they click CLAIM again (we should show an alert to claim later)
                                        // For a seamless flow, we try to claim aggressively after they return
                                        setTimeout(handleClaim, 3000);
                                    }
                                }}
                                size="sm"
                                fullWidth
                                variant="secondary"
                                icon={mission.targetUrl.includes('t.me') ? 'telegram' : 'open_in_new'}
                                isLoading={isClaiming}
                            >
                                GO
                            </Button>
                        </motion.div>
                    ) : null}
                </div>
            </div>
        </motion.div>
    );
}
